import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";

export const INR = (n) =>
  typeof n === "number" ? "\u20b9" + n.toLocaleString("en-IN") : n;

export const UNIT_LABEL = { "one-time": "one-time", month: "/mo", year: "/yr", hour: "/hr", custom: "" };

const NAV = [
  { to: "/in", label: "Home", end: true },
  { to: "/in/services", label: "Services" },
  { to: "/in/pricing", label: "Pricing" },
  { to: "/in/audit", label: "Website Audit" },
  { to: "/in/contact", label: "Get a Quote" },
];

// India instance shell — reuses the Core design system; NO links to the US site (WebsiteTech.pro).
export default function IndiaLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  // Keep the India preview out of search indexes (production indias.com controls its own indexing).
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow"; m.setAttribute("data-india", "1");
    document.head.appendChild(m);
    return () => { document.head.querySelector('meta[data-india="1"]')?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 inset-x-0 z-50 glass-nav border-b border-slate-200/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/in" data-testid="india-logo" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#0B203E] flex items-center justify-center text-white font-heading font-bold">W</div>
            <div className="leading-tight">
              <p className="font-heading font-bold text-slate-900 text-sm">WebsiteTech <span className="text-brand">India</span></p>
              <p className="text-[10px] text-slate-400 -mt-0.5">indias.com/websitetech</p>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`india-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `text-sm font-semibold transition-colors ${isActive ? "text-brand" : "text-slate-600 hover:text-brand"}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/in/contact" className="bg-brand text-white text-sm font-semibold rounded-xl px-4 py-2 hover:-translate-y-0.5 transition-transform">Get a Quote</Link>
          </nav>
          <button onClick={() => setOpen(!open)} className="md:hidden text-slate-700" data-testid="india-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
        {open && (
          <div className="md:hidden bg-white border-t border-slate-100 px-4 py-3 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className="text-sm font-semibold text-slate-700">{n.label}</NavLink>
            ))}
          </div>
        )}
      </header>

      <main className="pt-16">
        <Outlet />
      </main>

      <footer className="bg-[#0B203E] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <p className="font-heading font-bold text-lg">WebsiteTech <span className="text-blue-300">India</span></p>
            <p className="text-blue-100/70 text-sm mt-2 max-w-xs">AI-Powered Websites & Digital Growth for India. Websites • AI • Analytics • SEO • E-Commerce • Automation.</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-blue-300 font-semibold mb-3">Explore</p>
            <ul className="space-y-2 text-sm text-blue-100/80">
              {NAV.slice(1).map((n) => <li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-blue-300 font-semibold mb-3">India</p>
            <p className="text-sm text-blue-100/80">₹ INR pricing · GST-ready invoicing · UPI & Indian gateways · WhatsApp support · 10+ Indian languages.</p>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-blue-100/50">
          © {new Date().getFullYear()} WebsiteTech India · Part of the Indias.com network
        </div>
      </footer>
    </div>
  );
}
